import { useState, useEffect } from 'react';
import { ArrowRightLeft, X } from 'lucide-react';
import { useLeagueStore } from '../../lib/leagueStore';
import { useThemeStore } from '../../lib/themeStore';
import { translate } from '../../lib/translations';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';
import { TradeHistoryCard } from './TradeHistoryCard';
import { GroupedTrade } from '../../lib/utils/tradeUtils';

interface TradeHistoryProps {
  language: string;
}

interface User {
  user_id: number;
  username: string;
}

export function TradeHistory({ language }: TradeHistoryProps) {
  const selectedLeague = useLeagueStore((state) => state.selectedLeague);
  const { theme } = useThemeStore();
  
  const [trades, setTrades] = useState<GroupedTrade[]>([]);
  const [filteredTrades, setFilteredTrades] = useState<GroupedTrade[]>([]);
  const [loading, setLoading] = useState(true);
  const [userFilter, setUserFilter] = useState<string>('');
  const [users, setUsers] = useState<User[]>([]);

  useEffect(() => {
    if (selectedLeague?.id) {
      loadAcceptedTrades();
    }
  }, [selectedLeague]);

  useEffect(() => {
    if (trades.length > 0) {
      loadLeagueUsers();
    }
  }, [trades]);

  useEffect(() => {
    applyFilters();
  }, [trades, userFilter]);

  const loadLeagueUsers = async () => {
    try {
      // Extraire les utilisateurs depuis les données d'échanges déjà chargées
      if (trades.length > 0) {
        const uniqueUsers = new Map<number, string>();
        
        trades.forEach(trade => {
          uniqueUsers.set(trade.sender_user_id, trade.sender_username);
          uniqueUsers.set(trade.receiver_user_id, trade.receiver_username);
        });

        const usersList = Array.from(uniqueUsers.entries()).map(([user_id, username]) => ({
          user_id,
          username
        })).sort((a, b) => a.username.localeCompare(b.username));

        setUsers(usersList);
      }
    } catch (error) {
      console.error('Error loading league users:', error);
    }
  };

  const loadAcceptedTrades = async () => {
    if (!selectedLeague?.id) return;

    try {
      setLoading(true);
      
      // Récupérer tous les échanges acceptés de la ligue
      const { data, error } = await supabase
        .from('gettradeshistory_players_and_users')
        .select('*')
        .eq('leagueid', selectedLeague.id)
        .eq('is_accepted', true)
        .order('time_stamp', { ascending: false });

      if (error) throw error;

      // Grouper les échanges par transaction (th_id)
      const groupedTrades = groupTradesByTransaction(data || []);
      setTrades(groupedTrades);
      
    } catch (error) {
      console.error('Error loading trade history:', error);
      toast.error('Erreur lors du chargement de l\'historique des échanges');
    } finally {
      setLoading(false);
    }
  };

  const groupTradesByTransaction = (trades: any[]): GroupedTrade[] => {
    const grouped = trades.reduce((acc: any, trade) => {
      const key = trade.trades_id;
      
      if (!acc[key]) {
        acc[key] = {
          th_id: trade.th_id,
          trades_id: trade.trades_id,
          leagueid: trade.leagueid,
          time_stamp: trade.time_stamp,
          is_accepted: trade.is_accepted,
          is_refused: trade.is_refused,
          sender_username: trade.sender_username,
          sender_user_id: trade.sender_user_id,
          receiver_username: trade.receiver_username,
          receiver_user_id: trade.receiver_user_id,
          trades: []
        };
      }

      acc[key].trades.push({
        sender_player_name: trade.sender_player_name,
        sender_player_id: trade.sender_player_id,
        sender_goals: trade.sender_goals,
        sender_assists: trade.sender_assists,
        sender_points: trade.sender_points,
        sender_salary: trade.sender_salary,
        sender_yearleft: trade.sender_yearleft,
        sender_isnhl: trade.sender_isnhl,
        sender_goalie_name: trade.sender_goalie_name,
        sender_goalie_id: trade.sender_goalie_id,
        sender_goalie_wins: trade.sender_goalie_wins,
        sender_goalie_savepct: trade.sender_goalie_savepct,
        sender_goalie_shutout: trade.sender_goalie_shutout,
        sender_goalie_salary: trade.sender_goalie_salary,
        sender_goalie_yearleft: trade.sender_goalie_yearleft,
        sender_pick_id: trade.sender_pick_id,
        sender_pick_description: trade.sender_pick_description,
        sender_pick_original_user_id: trade.sender_pick_original_user_id,
        sender_pick_original_username: trade.sender_pick_original_username,
        receiver_player_name: trade.receiver_player_name,
        receiver_player_id: trade.receiver_player_id,
        receiver_goals: trade.receiver_goals,
        receiver_assists: trade.receiver_assists,
        receiver_points: trade.receiver_points,
        receiver_salary: trade.receiver_salary,
        receiver_yearleft: trade.receiver_yearleft,
        receiver_isnhl: trade.receiver_isnhl,
        receiver_goalie_name: trade.receiver_goalie_name,
        receiver_goalie_id: trade.receiver_goalie_id,
        receiver_goalie_wins: trade.receiver_goalie_wins,
        receiver_goalie_savepct: trade.receiver_goalie_savepct,
        receiver_goalie_shutout: trade.receiver_goalie_shutout,
        receiver_goalie_salary: trade.receiver_goalie_salary,
        receiver_goalie_yearleft: trade.receiver_goalie_yearleft,
        receiver_pick_id: trade.receiver_pick_id,
        receiver_pick_description: trade.receiver_pick_description,
        receiver_pick_original_user_id: trade.receiver_pick_original_user_id,
        receiver_pick_original_username: trade.receiver_pick_original_username,
      });

      return acc;
    }, {});

    return (Object.values(grouped) as GroupedTrade[]).sort((a: GroupedTrade, b: GroupedTrade) => 
      new Date(b.time_stamp).getTime() - new Date(a.time_stamp).getTime()
    );
  };

  const applyFilters = () => {
    let filtered = [...trades];

    // Filtre par utilisateur
    if (userFilter) {
      filtered = filtered.filter(trade => 
        trade.sender_user_id === parseInt(userFilter) || 
        trade.receiver_user_id === parseInt(userFilter)
      );
    }

    setFilteredTrades(filtered);
  };

  const clearFilters = () => {
    setUserFilter('');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">{translate('loading', language as "fr" | "en")}</span>
        </div>
        <p className="mt-3 text-muted">Chargement de l'historique des échanges...</p>
      </div>
    );
  }

  return (
    <div className="trade-history">
      {/* Header avec statistiques */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card border-0 shadow-sm" style={{
            background: theme === 'dark' 
              ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.8) 100%)'
              : 'linear-gradient(135deg, rgba(248, 250, 252, 0.8) 0%, rgba(241, 245, 249, 0.8) 100%)',
            backdropFilter: 'blur(10px)'
          }}>
            <div className="card-body p-4">
              <div className="row align-items-center">
                <div className="col-md-6">
                  <div className="d-flex align-items-center">
                    <ArrowRightLeft size={24} className="text-primary me-3" />
                    <div>
                      <h5 className={`mb-1 ${theme === 'dark' ? 'text-white' : 'text-dark'}`}>
                        Historique des échanges
                      </h5>
                      <p className="text-muted mb-0">
                        {filteredTrades.length} transaction{filteredTrades.length > 1 ? 's' : ''} acceptée{filteredTrades.length > 1 ? 's' : ''}
                        {userFilter && (
                          <span className="ms-2 badge bg-primary">
                            DG: {users.find(u => u.user_id.toString() === userFilter)?.username}
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="col-md-6 text-md-end">
                  <div className="d-flex align-items-center justify-content-end gap-2">
                    <label htmlFor="dgFilter" className="form-label mb-0 fw-semibold">
                      👨‍💼 Filtrer par DG:
                    </label>
                    <select
                      id="dgFilter"
                      className="form-select"
                      value={userFilter}
                      onChange={(e) => setUserFilter(e.target.value)}
                      style={{
                        maxWidth: '250px',
                        borderRadius: '8px',
                        border: theme === 'dark' ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid #dee2e6',
                        background: theme === 'dark' ? 'rgba(30, 41, 59, 0.8)' : 'white',
                        color: theme === 'dark' ? 'white' : '#212529'
                      }}
                    >
                      <option value="">🏒 Tous les DG</option>
                      {users.map(user => (
                        <option key={user.user_id} value={user.user_id}>
                          👨‍💼 {user.username}
                        </option>
                      ))}
                    </select>
                    {userFilter && (
                      <button
                        className="btn btn-outline-secondary btn-sm"
                        onClick={clearFilters}
                        title="Effacer le filtre"
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Liste des échanges */}
      {filteredTrades.length === 0 ? (
        <div className="text-center p-5">
          <ArrowRightLeft size={48} className="text-muted mb-3" />
          <h3 className="text-muted">
            {trades.length === 0 ? 'Aucun échange accepté' : 'Aucun résultat'}
          </h3>
          <p className="text-muted">
            {trades.length === 0 
              ? 'Il n\'y a pas encore d\'échanges acceptés dans cette ligue.'
              : 'Aucun échange ne correspond aux critères de recherche.'
            }
          </p>
        </div>
      ) : (
        <div className="trade-history-list">
          {filteredTrades.map((trade) => (
            <TradeHistoryCard
              key={`trade-${trade.trades_id || trade.th_id}-${trade.time_stamp}`}
              trade={trade}
              language={language}
              formatDate={formatDate}
            />
          ))}
        </div>
      )}
    </div>
  );
}
